import { type FunctionComponent, type PropsWithChildren } from 'react';
import { type ProjectRoomFieldsFragment } from '../../../__generated__/graphql-client-types';
import { CloudinaryImage } from '../../images/cloudinary-image/cloudinary-image.component';
import { roomTileImage } from './project-rooms-widget.css';

type MaybeClickableImageTileProps = {
	room: ProjectRoomFieldsFragment;
	tileClick: (event: React.MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => void;
	setSelectedRooms?: React.Dispatch<React.SetStateAction<number[]>> | ((roomIds: number[]) => void);
	tileKeyPress: (event: React.KeyboardEvent<HTMLDivElement>) => void;
	isRoomSelected: (roomId: number) => boolean;
};

const MaybeClickableImageTile: FunctionComponent<PropsWithChildren<MaybeClickableImageTileProps>> = ({
	children,
	room,
	tileClick,
	tileKeyPress,
	isRoomSelected,
	setSelectedRooms
}) => {
	return setSelectedRooms ? (
		<div
			onClick={tileClick}
			onKeyPress={tileKeyPress}
			className={`pointer flex flex-column items-center ma1 br2 hover-bg-theme-primary-lighter ph1 w-100 ${
				isRoomSelected(room.id) ? 'bg-theme-primary-lighter theme-primary' : ''
			}`}
			role="checkbox"
			aria-checked={isRoomSelected(room.id)}
			data-id={room.id}
			data-testid={`roomtile_${room.id}`}
			data-automation={`roomtile-${room.name}`}
			tabIndex={0}>
			{children}
		</div>
	) : (
		<div
			className={`flex flex-column items-center ma1 br2 ph1 w-100`}
			data-id={room.id}
			data-testid={`roomtile_${room.id}`}
			data-automation={`roomtile-${room.name}`}>
			{children}
		</div>
	);
};

export type ProjectRoomsWidgetProps = {
	rooms: ProjectRoomFieldsFragment[] | undefined;
	selectedRooms: number[];
	setSelectedRooms?: React.Dispatch<React.SetStateAction<number[]>> | ((roomIds: number[]) => void);
	titleClassName?: string;
	cardClassName?: string;
};
export const ProjectRoomsWidget: FunctionComponent<ProjectRoomsWidgetProps> = ({
	rooms,
	selectedRooms,
	setSelectedRooms,
	titleClassName = 'pt3 pl4 f5 lh-title font-medium self-start',
	cardClassName = 'w-33 w-25-ns'
}) => {
	const tileClick = (event: React.MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => {
		toggleRoomSelection(event.currentTarget);
	};
	const tileKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
		// toggle selection on spacebar or enter
		if (event.key === ' ' || event.key === 'Enter') {
			toggleRoomSelection(event.currentTarget);
		}
	};

	const toggleRoomSelection = (roomTile: HTMLDivElement) => {
		const roomId = Number(roomTile.dataset.id);
		const isSelected = isRoomSelected(roomId);
		if (isSelected) {
			// clears highlight when deselecting a room.
			roomTile.dispatchEvent(new MouseEvent('mouseleave'));
			roomTile.blur();
		}
		if (setSelectedRooms) setSelectedRooms(isSelected ? selectedRooms.filter((room) => room !== roomId) : selectedRooms.concat(roomId));
	};

	const isRoomSelected = (roomId: number): boolean => {
		return selectedRooms.indexOf(roomId) !== -1;
	};

	return (
		<div className={`flex flex-column items-center bg-theme-white`}>
			<div className={titleClassName}> Which rooms are you working on?</div>
			<div className="flex flex-column flex-wrap items-center w-100 relative">
				<ul className="list flex flex-row flex-wrap space-between justify-center ma0 w-100 f7 f6-ns ph3">
					{rooms?.map((room) => (
						<li key={room.id} className={`flex ma0 ${cardClassName} justify-center`}>
							<MaybeClickableImageTile
								tileClick={tileClick}
								tileKeyPress={tileKeyPress}
								isRoomSelected={isRoomSelected}
								setSelectedRooms={setSelectedRooms}
								room={room}>
								<div className={`${roomTileImage} flex flex-column items-center justify-center`}>
									<CloudinaryImage
										publicID={room.image.id}
										description={room.image.description}
										options={{ format: 'svg' }}
									/>
								</div>
								<span className="tc ttu pv1">{room.name}</span>
							</MaybeClickableImageTile>
						</li>
					))}
				</ul>
			</div>
		</div>
	);
};
